import CircularProgress from '@mui/material/CircularProgress';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { currentDatasheetProvenance } from '@cadstrom/cadstrom-shared';

import { DatasheetViewer } from '#components/datasheet-viewer/datasheet-viewer.js';
import { DatasheetProvenanceNotice } from '#package-store/package-editor/datasheet-tab/datasheet-provenance-notice.js';
import { DatasheetTabContext } from '#package-store/package-editor/datasheet-tab/datasheet-tab-extraction-context.js';
import { DatasheetValidationBanner } from '#package-store/package-editor/datasheet-tab/datasheet-validation-banner.js';
import { DatasheetValidationChip } from '#package-store/package-editor/datasheet-tab/datasheet-validation-chip.js';
import { useDatasheetValidation } from '#package-store/package-editor/datasheet-tab/use-datasheet-validation.js';

export const Route = createFileRoute(
	'/_authorization-gate/package-store/$packageFamilyId/packages/$packageId/datasheet/',
)({
	staticData: {
		navigation: {
			label: 'Package: Datasheet',
		},
	},
	loader: ({ context: { queryClient, getPackageOptions } }) => {
		void queryClient.prefetchQuery(getPackageOptions);
		const cached = queryClient.getQueryData(getPackageOptions.queryKey);
		return { pkg: cached?.pkg };
	},
	head: ({ loaderData }) => ({
		meta: [
			{
				title:
					(loaderData?.pkg ? loaderData.pkg.manufacturerPartNumber + ': ' : '') +
					'Datasheet • Package Editor • Cadstrom',
			},
		],
	}),
	component: DatasheetTab,
});

function DatasheetTab() {
	const { getPackageOptions } = Route.useRouteContext();

	const {
		data: { pkg, datasheetProvenance },
	} = useSuspenseQuery(getPackageOptions);

	const { validation, pending, setOverride, clearOverride, rerun, viewWorkflowRun } = useDatasheetValidation(pkg.id);

	return (
		<div className='relative flex grow flex-col gap-4 p-3'>
			{!pending && (
				<DatasheetValidationBanner
					// Keyed so expand/pin state resets when navigating between packages.
					key={pkg.id}
					packageId={pkg.id}
					validation={validation}
					onRerun={rerun}
					onViewWorkflowRun={viewWorkflowRun}
				/>
			)}

			<DatasheetProvenanceNotice
				// Replacing the datasheet patches only `pkg` in the cache, so provenance for the old file must not show.
				provenance={currentDatasheetProvenance(datasheetProvenance ?? undefined, pkg.fileId) ?? null}
			/>

			<DatasheetViewer
				key={pkg.fileId}
				fileId={pkg.fileId}
				allowReplaceDatasheet={{ pkgId: pkg.id }}
				pdfToolbarExtra={
					<DatasheetValidationChip
						validation={validation}
						pending={pending}
						onSetOverride={setOverride}
						onClearOverride={clearOverride}
						onRerun={rerun}
						onViewWorkflowRun={viewWorkflowRun}
					/>
				}
			/>

			<DatasheetTabContext />
		</div>
	);
}
